import {
  FreshServiceProposal,
  SubmitFreshServiceProposal,
} from "./fresh-service-proposal"
import { getEffectiveOrderDiscount } from "./full-approval-discount"
import { PriceUtils } from "./price-utils"
import { Receipt } from "./receipt"
import { ReceiptMapper } from "./receipt-mapper"
import { ProposalDiscounts } from "./service-proposal-context"
import {
  withMovedOutPackageActions,
  withPackageSelectionsMatchingProposal,
} from "./service-proposal-grouping"

/**
 * The receipt the customer's proposal screen prices a selection with. Shared
 * so the API records the same totals the cyclist saw.
 *
 * Undecided choices are priced at their cheapest alternative, matching the
 * "Från X kr" the cards show. Pricing them at the mechanic's recommendation
 * would put a total under the customer's "Från" label that they can still get
 * below, which is not what "från" means.
 *
 * The full-approval discount counts only while the selection approves
 * everything, so a level card and the footer agree with what the API records.
 */
export const buildProposalReceipt = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
  discounts: ProposalDiscounts,
): Receipt => {
  const matchingValues = withPackageSelectionsMatchingProposal(proposal, values)
  return PriceUtils.applyDiscountsToReceipt(
    ReceiptMapper.proposalToReceipt(
      withMovedOutPackageActions(proposal, matchingValues),
      matchingValues,
      { undecidedChoicePricing: "CHEAPEST" },
    ),
    {
      laborDiscount: discounts.laborDiscount,
      productsDiscount: discounts.productsDiscount,
      orderDiscount: getEffectiveOrderDiscount(
        proposal,
        matchingValues,
        discounts,
      ),
      exclusions: discounts.exclusions,
    },
  )
}

export const getReceiptTotal = (receipt: Receipt): number =>
  receipt.totalCostAfterOrderDiscount ?? receipt.totalCost

/**
 * The selection with no "Tillval" products. "Tillval" has a step and a total
 * of its own; the service is priced without it.
 */
export const withoutUpsells = (
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => ({ ...values, selectedUpsellProductIds: [] })
